import { GbeErrorCodes } from '@core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@core/models/gbe/gbe-error.model';
import {
  RecipeParameterSchema,
  RecipeParameterSubmissionError
} from '@core/models/recipe-parameters.model';

import { parseGbeError } from './parse-gbe-error';
import { mapSchemaValidationErrors } from './recipe-parameter-schema.utils';

const RECIPE_AUTHORING_CODES: readonly string[] = [
  GbeErrorCodes.MALFORMED_RECIPE_PLACEHOLDER_ERROR,
  GbeErrorCodes.CONFLICTING_RECIPE_PARAMETER_ERROR
];

const RECIPE_PARAMETER_CODES: readonly string[] = [
  ...RECIPE_AUTHORING_CODES,
  GbeErrorCodes.SCHEMA_VALIDATION_ERROR,
  GbeErrorCodes.INVALID_ARGUMENT_ERROR
];

/**
 * Parses a rejection only when it carries a GBE error body.
 *
 * `parseGbeError` dereferences `error.error` unguarded, so it throws on any
 * other object — a bare `Error` from a network failure, for instance.
 */
function gbeErrorOf(error: unknown): GbeError | null {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  return body == null ? null : parseGbeError(error);
}

/**
 * Whether a failure concerns recipe parameters, and so should keep the
 * plan-creation form open for correction instead of closing it.
 *
 * Separate from `toRecipeParameterSubmissionError` because the decision needs
 * only the error code, while mapping details onto fields needs the schema —
 * which lives in the form component, not its parent.
 */
export function isRecipeParameterError(error: unknown): boolean {
  const gbeError = gbeErrorOf(error);
  return gbeError !== null && RECIPE_PARAMETER_CODES.includes(gbeError.errorCode);
}

/**
 * Splits a rejected plan creation into what the planner can fix and what only a
 * recipe author can.
 *
 * Returns `null` when the failure has nothing to do with recipe parameters, so
 * the caller can fall back to its usual error handling.
 */
export function toRecipeParameterSubmissionError(
  schema: RecipeParameterSchema | null | undefined,
  error: unknown
): RecipeParameterSubmissionError | null {
  const gbeError = gbeErrorOf(error);
  if (gbeError === null) return null;

  if (RECIPE_AUTHORING_CODES.includes(gbeError.errorCode)) {
    return { fields: {}, recipeMisconfigured: true };
  }

  if (gbeError.errorCode === GbeErrorCodes.SCHEMA_VALIDATION_ERROR) {
    const { fields, unmatched } = mapSchemaValidationErrors(schema, gbeError.details);
    const formMessage =
      unmatched.length > 0
        ? unmatched.join(' ')
        : Object.keys(fields).length === 0
          ? gbeError.errorMessage
          : undefined;
    return { fields, formMessage, recipeMisconfigured: false };
  }

  if (gbeError.errorCode === GbeErrorCodes.INVALID_ARGUMENT_ERROR) {
    return { fields: {}, formMessage: gbeError.errorMessage, recipeMisconfigured: false };
  }

  return null;
}
